import React, { useEffect } from "react";
import { useForm, useFormState } from "react-final-form";

/** France `code_iso` in reference data (INSEE numeric country code). */
export const FRANCE_BIRTH_COUNTRY_CODE_ISO = "100";

export const sectionTitleClassName =
    "flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-foreground";
export const sectionIndexClassName =
    "inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-primary font-black";
export const fieldLabelClassName = "text-[11px] font-bold uppercase tracking-wider text-muted-foreground";
export const fieldInputClassName =
    "field-control__input h-12 rounded-full border border-input bg-muted/50 px-5 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring";
export const textareaClassName =
    "w-full min-h-[170px] rounded-[28px] border border-input bg-muted/50 px-5 py-4 text-sm leading-relaxed placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
export const passwordViewerClassName = "cursor-pointer absolute right-4 top-1/2 -translate-y-1/2";

export type DateInputWithPicker = HTMLInputElement & { showPicker?: () => void };

export function isNonEmptyString(value: unknown) {
    return String(value ?? "").trim().length > 0;
}

export function isPlausibleFrenchNir(nirRaw: string) {
    const nir = String(nirRaw ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, "");

    return /^[0-9]{5}([0-9]{2}|2A|2B)[0-9]{6}[0-9]{2}$/.test(nir);
}

function normalizeNir(nirRaw: unknown): string {
    return String(nirRaw ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, "");
}

export function extractBirthDepartmentFromNir(nirRaw: unknown): string | null {
    const cleaned = normalizeNir(nirRaw);
    if (cleaned.length < 7 || !/^[0-9]{5}/.test(cleaned)) {
        return null;
    }
    const tail = cleaned.slice(5);
    if (tail.startsWith("2A")) {
        return "2A";
    }
    if (tail.startsWith("2B")) {
        return "2B";
    }
    const two = tail.slice(0, 2);
    if (/^[0-9]{2}$/.test(two)) {
        return two;
    }
    return null;
}

function hasStandardNirBirthMonth(nirRaw: unknown): boolean {
    const nir = normalizeNir(nirRaw);
    if (nir.length < 5) {
        return false;
    }
    const month = Number(nir.slice(3, 5));
    return month >= 1 && month <= 12;
}

export function isNirBirthdayConsistent(nirRaw: unknown, birthdayRaw: unknown): boolean {
    const birthday = String(birthdayRaw ?? "").trim();
    if (!birthday) {
        return true;
    }

    const nir = normalizeNir(nirRaw);
    if (nir.length < 5) {
        return true;
    }

    const birthDate = new Date(`${birthday}T00:00:00`);
    if (Number.isNaN(birthDate.getTime())) {
        return true;
    }

    const nirYear = nir.slice(1, 3);
    const birthYear = String(birthDate.getFullYear() % 100).padStart(2, "0");
    if (nirYear !== birthYear) {
        return false;
    }

    if (!hasStandardNirBirthMonth(nir)) {
        return true;
    }

    const nirMonth = nir.slice(3, 5);
    const birthMonth = String(birthDate.getMonth() + 1).padStart(2, "0");

    return nirMonth === birthMonth;
}

export function isNirDepartmentConsistent(
    nirRaw: unknown,
    countryBirthCodeRaw: unknown,
    departmentBirthCodeRaw: unknown,
): boolean {
    const country = String(countryBirthCodeRaw ?? "").trim();
    if (country !== FRANCE_BIRTH_COUNTRY_CODE_ISO) {
        return true;
    }

    const department = String(departmentBirthCodeRaw ?? "").trim().toUpperCase();
    if (!department) {
        return true;
    }

    const nirDepartment = extractBirthDepartmentFromNir(nirRaw);
    if (!nirDepartment || nirDepartment === "99") {
        return true;
    }

    return department === nirDepartment;
}

export function getNirBirthConsistencyErrors(
    values: {
        nir?: unknown;
        birthday?: unknown;
        country_birth_code?: unknown;
        department_birth_code?: unknown;
    },
    messages: {
        birthdayMismatch: string;
        departmentMismatch: string;
    },
): Record<string, string> {
    const errors: Record<string, string> = {};
    const nir = String(values?.nir ?? "").trim();

    if (!nir || !isPlausibleFrenchNir(nir)) {
        return errors;
    }

    if (!isNirBirthdayConsistent(nir, values?.birthday)) {
        errors.birthday = messages.birthdayMismatch;
    }

    if (!isNirDepartmentConsistent(nir, values?.country_birth_code, values?.department_birth_code)) {
        errors.department_birth_code = messages.departmentMismatch;
    }

    return errors;
}

export function isNirConsistentWithBirthData(values: {
    nir?: unknown;
    birthday?: unknown;
    country_birth_code?: unknown;
    department_birth_code?: unknown;
}): boolean {
    const nir = String(values?.nir ?? "").trim();
    if (!nir || !isPlausibleFrenchNir(nir)) {
        return false;
    }

    return (
        isNirBirthdayConsistent(nir, values?.birthday) &&
        isNirDepartmentConsistent(nir, values?.country_birth_code, values?.department_birth_code)
    );
}

/** Sets France as the default birth country when the field is empty. */
export const ProviderDefaultBirthCountry: React.FC = () => {
    const form = useForm();
    const { values } = useFormState({ subscription: { values: true } });

    useEffect(() => {
        if (!isNonEmptyString(values?.country_birth_code)) {
            form.change("country_birth_code", FRANCE_BIRTH_COUNTRY_CODE_ISO);
        }
    }, [form, values?.country_birth_code]);

    return null;
};

/**
 * When birth country is France, keeps `department_birth_code` in sync with the
 * department digits from the NIR (positions 6–7: 01–99, 2A, 2B). Clears the
 * field when no country or when birth country is not France.
 */
export const ProviderBirthDepartmentFromNir: React.FC = () => {
    const form = useForm();
    const { values } = useFormState({ subscription: { values: true } });

    useEffect(() => {
        const country = String(values?.country_birth_code ?? "").trim();
        if (!country || country !== FRANCE_BIRTH_COUNTRY_CODE_ISO) {
            form.change("department_birth_code", undefined);
            return;
        }
        const dept = extractBirthDepartmentFromNir(values?.nir);
        form.change("department_birth_code", dept ?? undefined);
    }, [form, values?.country_birth_code, values?.nir]);

    return null;
};
